import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@/components";
import {
    Accordion,
    Autocomplete,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Switch,
    Text,
    Textarea,
} from "@/ui";
import { useValidator } from "@/hooks";
import { useStoreContext } from "context";
import {
    findInvalidFieldElement,
    getInvalidFieldNames,
    scrollToFirstInvalidField,
} from "@/utils";
import _ from "lodash";
import styles from "./CustomizationsForm.module.scss";

const initialState = {
    // 1. Global Customizations
    wishlist_active: false,
    is_med_and_rec_selection_active: false,
    active_med_and_rec_selection_locations: [],
    treez_filter_sort:
        "deals,category,classification,brand,price,thc,effect,flavor,subType,general",
    treez_filters_title_mapping:
        '{\n  "productTypeName": "Categories",\n  "brand": "Brand",\n  "subType": "SubType",\n  "classification": "Classification",\n  "generals": "Other",\n  "effects": "Effects",\n  "flavors": "Flavors",\n  "deals": "On Sale"\n }',
    display_out_of_stock_products: false,
    display_low_in_stock_products: false,

    //Regulation
    enable_mmid_tooltip: false,
    allow_medical_sign_ups: false,
    allowed_medical_sign_up_locations: [],
    is_required_medical_document: false,
    required_medical_document_locations: [],

    // 2. Checkout Customizations
    is_grlimit_active: false,
    allow_guest_checkout: false,
    allowed_guest_checkout_locations: [],
    checkout_countdown_time: 10,
    allow_discount_loyalty_stacking: true,

    // 3. Product Detail Customizations
    keep_shopping_active: false,
    show_product_warning: false,
    disable_product_reviews: false,
    show_striketrough_price: false,

    // 4. Kiosk Customizations
    is_add_to_cart_in_kiosk_disabled: false,
    kiosk_hide_optional_contact_fields: false,
};

const SECTION_KEYS = [
    "global",
    "regulation",
    "checkout",
    "validation",
    "product_detail",
    "kios",
];

/** Accordion section for each validated field (Customizations-only reveal). */
const INVALID_FIELD_TO_SECTION = {
    "feature_flags.active_med_and_rec_selection_locations": "global",
    "feature_flags.required_medical_document_locations": "regulation",
    "feature_flags.allowed_medical_sign_up_locations": "regulation",
    "feature_flags.allowed_guest_checkout_locations": "checkout",
    "feature_flags.allowed_curbside_pickup_locations": "checkout",
    "feature_flags.checkout_countdown_time": "checkout",
    "feature_flags.curbside_pickup_placeholder": "checkout",
    "feature_flags.payment_detail_note_pickup": "checkout",
    "feature_flags.payment_detail_note_delivery": "checkout",
};

const getSectionForInvalidFields = fields => {
    for (const field of fields) {
        const section = INVALID_FIELD_TO_SECTION[field];
        if (section) {
            return section;
        }
    }
    return null;
};

const LOCATION_GATED_FLAGS = {
    medAndRec: {
        enabledKey: "is_med_and_rec_selection_active",
        locationsKey: "active_med_and_rec_selection_locations",
        message: "Select at least one store with Med & Rec enabled.",
    },
    guestCheckout: {
        enabledKey: "allow_guest_checkout",
        locationsKey: "allowed_guest_checkout_locations",
        message: "Select at least one store with Guest Checkout enabled.",
    },
    medicalDocument: {
        enabledKey: "is_required_medical_document",
        locationsKey: "required_medical_document_locations",
        message:
            "Select at least one store with Medical Document Required enabled.",
    },
    medicalSignUps: {
        enabledKey: "allow_medical_sign_ups",
        locationsKey: "allowed_medical_sign_up_locations",
        message: "Select at least one store with Medical Sign Ups enabled.",
    },
    curbside: {
        enabledKey: "allow_curbside_pickup",
        locationsKey: "allowed_curbside_pickup_locations",
        message: "Select at least one store with Curbside enabled.",
    },
};

const SaveIcon = () => <Icon icon="save" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const locationRequiredWhenEnabledRule = ({
    enabledKey,
    locationsKey,
    message,
}) => ({
    field: `feature_flags.${locationsKey}`,
    method: (_value, state) => {
        if (!_.get(state, `feature_flags.${enabledKey}`)) {
            return true;
        }
        const ids = _.get(state, `feature_flags.${locationsKey}`);
        return Array.isArray(ids) && ids.length > 0;
    },
    validWhen: true,
    message,
});

const locationsFieldError = (validator, locationsKey, enabled, ids, message) =>
    fieldError(validator, `feature_flags.${locationsKey}`) ||
    (enabled && ids.length === 0 ? message : undefined);

function SettingRow({ title, description, children }) {
    return (
        <div className={styles.row}>
            <div className={styles.rowCopy}>
                <p className={styles.rowTitle}>{title}</p>
                {description ? (
                    <p className={styles.rowDescription}>{description}</p>
                ) : null}
            </div>
            <div className={styles.rowControl}>{children}</div>
        </div>
    );
}

function SectionTable({ children, controlLabel = "Toggle" }) {
    return (
        <div className={styles.sectionBody}>
            <div className={styles.columnHeader}>
                <span>Customization & Description</span>
                <span className={styles.columnHeaderControl}>
                    {controlLabel}
                </span>
            </div>
            {children}
        </div>
    );
}

function FlagSwitch({ checked, name, onChange, label }) {
    return (
        <Switch
            aria-label={label}
            checked={Boolean(checked)}
            name={name}
            onChange={onChange}
            showLabel={false}
        />
    );
}

function LocationProvidersField({
    title,
    description,
    locationsTitle,
    locationsDescription,
    enabled,
    enabledName,
    enabledLabel,
    onEnabledChange,
    locations,
    locationsName,
    onLocationsChange,
    options,
    error,
    autocompleteLabel = "Store Provider",
}) {
    return (
        <>
            <SettingRow description={description} title={title}>
                <FlagSwitch
                    checked={enabled}
                    label={enabledLabel}
                    name={enabledName}
                    onChange={onEnabledChange}
                />
            </SettingRow>
            {enabled ? (
                <SettingRow
                    description={locationsDescription}
                    title={locationsTitle}
                >
                    <div className={styles.rowControlField}>
                        <Autocomplete
                            disabled={options.length === 0}
                            error={error}
                            fullWidth
                            helperText={
                                options.length === 0
                                    ? "No store providers available."
                                    : undefined
                            }
                            label={autocompleteLabel}
                            maxVisibleChips={1}
                            multiple
                            name={locationsName}
                            onChange={onLocationsChange}
                            options={options}
                            placeholder="Select stores"
                            required
                            value={locations}
                        />
                    </div>
                </SettingRow>
            ) : null}
        </>
    );
}

const CustomizationsForm = ({
    inputs,
    loading,
    inputChange,
    setInputs,
    actionCreateOrUpdate,
}) => {
    const { providers } = useStoreContext();
    const formRootRef = useRef(null);
    const handledScrollRequestRef = useRef(0);
    const [scrollRequestId, setScrollRequestId] = useState(0);
    const [expandedSections, setExpandedSections] = useState({
        global: true,
        checkout: true,
        regulation: true,
        validation: true,
        product_detail: true,
        kios: true,
    });

    const storeProviderOptions = useMemo(
        () =>
            (providers || []).map(store => ({
                value: String(store.value),
                label: store.label,
            })),
        [providers],
    );

    const { validator, isValid, isSubmited, submited } = useValidator(
        [
            {
                field: "feature_flags.checkout_countdown_time",
                method: "isEmpty",
                validWhen: false,
                message: "This countdown time is required.",
            },
            {
                field: "feature_flags.checkout_countdown_time",
                method: value => {
                    const num = Number(value);
                    return !isNaN(num) && Number.isInteger(num);
                },
                validWhen: true,
                message: "Value must be an integer.",
            },
            {
                field: "feature_flags.checkout_countdown_time",
                method: value => {
                    const num = Number(value);
                    return num >= 10 && num <= 30;
                },
                validWhen: true,
                message: "Value must be between 10 and 30 minutes.",
            },
            locationRequiredWhenEnabledRule(LOCATION_GATED_FLAGS.curbside),
            locationRequiredWhenEnabledRule(LOCATION_GATED_FLAGS.medAndRec),
            locationRequiredWhenEnabledRule(LOCATION_GATED_FLAGS.guestCheckout),
            locationRequiredWhenEnabledRule(
                LOCATION_GATED_FLAGS.medicalDocument,
            ),
            locationRequiredWhenEnabledRule(
                LOCATION_GATED_FLAGS.medicalSignUps,
            ),
            {
                field: "feature_flags.curbside_pickup_placeholder",
                method: value => {
                    // Allow empty values (not required)
                    if (!value || value === "") {
                        return true;
                    }
                    // Validate that it does not exceed 40 characters
                    const text = String(value);
                    return text.length <= 40;
                },
                validWhen: true,
                message: "The placeholder text must not exceed 40 characters.",
            },
            {
                field: "feature_flags.payment_detail_note_pickup",
                method: value => {
                    if (!value || value === "") {
                        return true;
                    }
                    return String(value).length <= 75;
                },
                validWhen: true,
                message:
                    "The payment detail note must not exceed 75 characters.",
            },
            {
                field: "feature_flags.payment_detail_note_delivery",
                method: value => {
                    if (!value || value === "") {
                        return true;
                    }
                    return String(value).length <= 75;
                },
                validWhen: true,
                message:
                    "The payment detail note must not exceed 75 characters.",
            },
        ],
        inputs,
    );

    const hasFeatureFlags = Boolean(_.get(inputs, "feature_flags", null));

    useEffect(() => {
        if (hasFeatureFlags) {
            return;
        }
        setInputs(prev => ({
            ...prev,
            feature_flags: {
                ...initialState,
            },
        }));
    }, [hasFeatureFlags, setInputs]);

    const openSections = SECTION_KEYS.filter(key => expandedSections[key]);

    const handleAccordionChange = values => {
        setExpandedSections(prev => ({
            ...prev,
            ...Object.fromEntries(
                SECTION_KEYS.map(key => [key, values.includes(key)]),
            ),
        }));
    };

    const patchLocationGatedFlag = ({
        enabledKey,
        locationsKey,
        enabled,
        selectedIds,
    }) => {
        setInputs(prev => {
            const flags = prev.feature_flags ?? {};
            const nextFlags = { ...flags };

            if (selectedIds !== undefined) {
                nextFlags[locationsKey] = (selectedIds ?? []).map(String);
            }

            if (enabled !== undefined) {
                const currentIds = flags[locationsKey] || [];
                nextFlags[enabledKey] = enabled;
                nextFlags[locationsKey] =
                    enabled && currentIds.length === 0
                        ? storeProviderOptions.map(option => option.value)
                        : currentIds;
            }

            return {
                ...prev,
                feature_flags: nextFlags,
            };
        });
    };

    const createLocationToggleHandler =
        ({ enabledKey, locationsKey }) =>
        e =>
            patchLocationGatedFlag({
                enabledKey,
                locationsKey,
                enabled: Boolean(e.target.checked),
            });

    const createLocationProvidersChangeHandler =
        ({ locationsKey }) =>
        selectedIds =>
            patchLocationGatedFlag({
                locationsKey,
                selectedIds,
            });

    const handleMedAndRecToggle = createLocationToggleHandler(
        LOCATION_GATED_FLAGS.medAndRec,
    );
    const handleMedAndRecStoreProvidersChange =
        createLocationProvidersChangeHandler(LOCATION_GATED_FLAGS.medAndRec);

    const handleGuestCheckoutToggle = createLocationToggleHandler(
        LOCATION_GATED_FLAGS.guestCheckout,
    );
    const handleGuestCheckoutStoreProvidersChange =
        createLocationProvidersChangeHandler(
            LOCATION_GATED_FLAGS.guestCheckout,
        );

    const handleRequiredMedicalDocumentToggle = createLocationToggleHandler(
        LOCATION_GATED_FLAGS.medicalDocument,
    );
    const handleRequiredMedicalDocumentStoreProvidersChange =
        createLocationProvidersChangeHandler(
            LOCATION_GATED_FLAGS.medicalDocument,
        );

    const handleMedicalSignUpsToggle = createLocationToggleHandler(
        LOCATION_GATED_FLAGS.medicalSignUps,
    );
    const handleMedicalSignUpsStoreProvidersChange =
        createLocationProvidersChangeHandler(
            LOCATION_GATED_FLAGS.medicalSignUps,
        );

    const handleCurbsideToggle = createLocationToggleHandler(
        LOCATION_GATED_FLAGS.curbside,
    );
    const handleCurbsideStoreProvidersChange =
        createLocationProvidersChangeHandler(LOCATION_GATED_FLAGS.curbside);

    const createOrUpdate = () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            actionCreateOrUpdate();
            return;
        }

        setScrollRequestId(id => id + 1);
    };

    // Phase A: open accordion for the invalid field.
    // Phase B: once that section is open, scroll/focus via the shared util.
    useEffect(() => {
        if (!scrollRequestId || !submited) {
            return undefined;
        }

        if (handledScrollRequestRef.current === scrollRequestId) {
            return undefined;
        }

        const invalidFields = getInvalidFieldNames(validator);
        if (invalidFields.length === 0) {
            handledScrollRequestRef.current = scrollRequestId;
            return undefined;
        }

        // Use document order (not validator rule order) so we open the
        // visually first invalid field's accordion section.
        const firstInvalid = findInvalidFieldElement({
            root: formRootRef.current,
            fields: invalidFields,
        });
        const section = firstInvalid?.field
            ? INVALID_FIELD_TO_SECTION[firstInvalid.field] ?? null
            : getSectionForInvalidFields(invalidFields);

        if (section && !expandedSections[section]) {
            setExpandedSections(prev =>
                prev[section] ? prev : { ...prev, [section]: true },
            );
            return undefined;
        }

        const timeoutId = window.setTimeout(
            () => {
                handledScrollRequestRef.current = scrollRequestId;
                scrollToFirstInvalidField({
                    root: formRootRef.current,
                    fields: invalidFields,
                    revealDelayMs: 0,
                });
            },
            section ? 150 : 0,
        );

        return () => window.clearTimeout(timeoutId);
    }, [scrollRequestId, submited, expandedSections]);

    const flags = inputs.feature_flags ?? {};
    const medAndRecStoreProviderIds = (
        flags.active_med_and_rec_selection_locations || []
    ).map(String);
    const guestCheckoutStoreProviderIds = (
        flags.allowed_guest_checkout_locations || []
    ).map(String);
    const requiredMedicalDocumentStoreProviderIds = (
        flags.required_medical_document_locations || []
    ).map(String);
    const medicalSignUpsStoreProviderIds = (
        flags.allowed_medical_sign_up_locations || []
    ).map(String);
    const curbsideStoreProviderIds = (
        flags.allowed_curbside_pickup_locations || []
    ).map(String);
    const countdownError = fieldError(
        validator,
        "feature_flags.checkout_countdown_time",
    );
    const medAndRecStoreProvidersError = locationsFieldError(
        validator,
        LOCATION_GATED_FLAGS.medAndRec.locationsKey,
        flags.is_med_and_rec_selection_active,
        medAndRecStoreProviderIds,
        LOCATION_GATED_FLAGS.medAndRec.message,
    );
    const guestCheckoutStoreProvidersError = locationsFieldError(
        validator,
        LOCATION_GATED_FLAGS.guestCheckout.locationsKey,
        flags.allow_guest_checkout,
        guestCheckoutStoreProviderIds,
        LOCATION_GATED_FLAGS.guestCheckout.message,
    );
    const requiredMedicalDocumentStoreProvidersError = locationsFieldError(
        validator,
        LOCATION_GATED_FLAGS.medicalDocument.locationsKey,
        flags.is_required_medical_document,
        requiredMedicalDocumentStoreProviderIds,
        LOCATION_GATED_FLAGS.medicalDocument.message,
    );
    const medicalSignUpsStoreProvidersError = locationsFieldError(
        validator,
        LOCATION_GATED_FLAGS.medicalSignUps.locationsKey,
        flags.allow_medical_sign_ups,
        medicalSignUpsStoreProviderIds,
        LOCATION_GATED_FLAGS.medicalSignUps.message,
    );
    const curbsideStoreProvidersError = locationsFieldError(
        validator,
        LOCATION_GATED_FLAGS.curbside.locationsKey,
        flags.allow_curbside_pickup,
        curbsideStoreProviderIds,
        LOCATION_GATED_FLAGS.curbside.message,
    );
    const curbsideError = fieldError(
        validator,
        "feature_flags.curbside_pickup_placeholder",
    );
    const paymentNotePickupError = fieldError(
        validator,
        "feature_flags.payment_detail_note_pickup",
    );
    const paymentNoteDeliveryError = fieldError(
        validator,
        "feature_flags.payment_detail_note_delivery",
    );

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Customizations</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Customizations Information
                        </Text>
                    </div>
                    <HeaderActions
                        actions={[
                            {
                                id: "update",
                                label: "Update",
                                icon: loading ? undefined : <SaveIcon />,
                                loading,
                                disabled: loading,
                                onClick: createOrUpdate,
                            },
                        ]}
                        aria-label="Customizations actions"
                    />
                </div>
            }
            showHeaderDivider
        >
            <div ref={formRootRef}>
                {loading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <Accordion
                        className={styles.accordion}
                        onValueChange={handleAccordionChange}
                        type="multiple"
                        value={openSections}
                        variant="expandablePanel"
                    >
                        <Accordion.Item data-section="global" value="global">
                            <Accordion.Header>
                                <Accordion.Trigger>1. Global</Accordion.Trigger>
                            </Accordion.Header>
                            <Accordion.Content>
                                <SectionTable>
                                    <SettingRow
                                        description="If on, discounted products will show strikethrough pricing and original price."
                                        title="Strikethrough price"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.show_striketrough_price
                                            }
                                            label="Strikethrough price"
                                            name="feature_flags.show_striketrough_price"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, when a customer approves an order from the admin dashboard, the system will bypass barcode validation for the items.."
                                        title="Skip Barcode Validation"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.bypass_barcode_validation
                                            }
                                            label="Skip Barcode Validation"
                                            name="feature_flags.bypass_barcode_validation"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    {/* <SettingRow
                                        description='When enabled, products with low inventory will display a "Low Stock" badge. Customers will not be able to add these items to their cart.'
                                        title="Low Stock"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.display_low_in_stock_products
                                            }
                                            label="Low Stock Products"
                                            name="feature_flags.display_low_in_stock_products"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description='When enabled, products with no remaining inventory will display an "Out of Stock" badge. Customers will not be able to add these items to their cart.'
                                        title="Out of Stock"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.display_out_of_stock_products
                                            }
                                            label="Out of Stock Products"
                                            name="feature_flags.display_out_of_stock_products"
                                            onChange={inputChange}
                                        />
                                    </SettingRow> */}
                                    <LocationProvidersField
                                        description="If enabled, the Med & Rec toggle component will appear in the website header for the selected store providers."
                                        enabled={
                                            flags.is_med_and_rec_selection_active
                                        }
                                        enabledLabel="Med & Rec"
                                        enabledName="feature_flags.is_med_and_rec_selection_active"
                                        error={medAndRecStoreProvidersError}
                                        locations={medAndRecStoreProviderIds}
                                        locationsDescription="Select the stores where the Med & Rec toggle will be displayed. At least one is required."
                                        locationsName="feature_flags.active_med_and_rec_selection_locations"
                                        locationsTitle="Stores with Med & Rec enabled"
                                        onEnabledChange={handleMedAndRecToggle}
                                        onLocationsChange={
                                            handleMedAndRecStoreProvidersChange
                                        }
                                        options={storeProviderOptions}
                                        title="Med & Rec"
                                    />
                                    <SettingRow
                                        description="If on, enables all functions related to the wish list, whether on the header, the product card, or product details."
                                        title="Wishlist"
                                    >
                                        <FlagSwitch
                                            checked={flags.wishlist_active}
                                            label="Wishlist"
                                            name="feature_flags.wishlist_active"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, customers will be required to select a store or fulfillment type on their first visit, before they begin shopping."
                                        title="Store & Fulfillment Selection"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.force_to_select_store
                                            }
                                            label="Store & Fulfillment Selection"
                                            name="feature_flags.force_to_select_store"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, customers will be required to select a location or fulfillment type on each visit."
                                        title="Force Store & Fulfillment Type Selection"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.force_to_select_store_visit_each
                                            }
                                            label="Force Store & Fulfillment Type Selection"
                                            name="feature_flags.force_to_select_store_visit_each"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, the option to cancel an order will appear on the confirmation or order details page."
                                        title="Cancel Order"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.allow_cancel_order_from_confirmation_page
                                            }
                                            label="Cancel Order"
                                            name="feature_flags.allow_cancel_order_from_confirmation_page"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="Promotional products priced below the defined threshold will be hidden from the regular product listing page and will only be displayed on special pages where these products are part of a BOGO deal."
                                        title="Promo units"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Input
                                                fullWidth
                                                label="Promo units"
                                                name="feature_flags.discount_minimum_threshold_value"
                                                onChange={inputChange}
                                                prefix="$"
                                                value={
                                                    flags.discount_minimum_threshold_value ??
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                    <SettingRow
                                        description={
                                            <>
                                                If defined, it will present the
                                                filter sorting. The value must
                                                be specified in the
                                                comma-separated list.{" "}
                                                <strong>
                                                    Posible values:{" "}
                                                </strong>
                                                deals,category,classification,brand,price,thc,tac,cbd,effect,flavor,subType,general
                                            </>
                                        }
                                        title="Filter sort"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Textarea
                                                fullWidth
                                                label="Filter sort"
                                                name="feature_flags.treez_filter_sort"
                                                onChange={inputChange}
                                                placeholder="deals,category,classification,brand,price,thc,effect,flavor,subType,general"
                                                rows={2}
                                                value={
                                                    flags.treez_filter_sort ||
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                    <SettingRow
                                        description="If defined, it will present the filter title mapping. The value must be specified in the JSON format."
                                        title="Filters title mapping"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Textarea
                                                fullWidth
                                                label="Filters title mapping"
                                                name="feature_flags.treez_filters_title_mapping"
                                                onChange={inputChange}
                                                placeholder=""
                                                rows={10}
                                                value={
                                                    flags.treez_filters_title_mapping ||
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                </SectionTable>
                            </Accordion.Content>
                        </Accordion.Item>

                        <Accordion.Item
                            data-section="regulation"
                            value="regulation"
                        >
                            <Accordion.Header>
                                <Accordion.Trigger>
                                    2. Regulation
                                </Accordion.Trigger>
                            </Accordion.Header>
                            <Accordion.Content>
                                <SectionTable>
                                    <SettingRow
                                        description="If on, product health warnings for Proposition 65 Warning for California, will show on product details."
                                        title="Product warning"
                                    >
                                        <FlagSwitch
                                            checked={flags.show_product_warning}
                                            label="Product warning"
                                            name="feature_flags.show_product_warning"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, the SB 540 Regulation brochure will be displayed to customers upon checkout. This is a California Requirement."
                                        title="SB 540: Cannabis Products Health Warning"
                                    >
                                        <FlagSwitch
                                            checked={flags.sb_540_regulation}
                                            label="SB 540: Cannabis Products Health Warning"
                                            name="feature_flags.sb_540_regulation"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If your dispensary is in the state of IL, ensure that this setting is turned on as a required state of residence before customer checkout to display the correct purchase limit regulations."
                                        title="Require state of residence"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.require_state_at_checkout
                                            }
                                            label="Require state of residence"
                                            name="feature_flags.require_state_at_checkout"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, a tooltip appears on the signup form next to the Medical ID field for customers, explaining MMID and showing an example MMID card."
                                        title="Enable MMID Tooltip (CA Only)"
                                    >
                                        <FlagSwitch
                                            checked={flags.enable_mmid_tooltip}
                                            label="Enable MMID Tooltip (CA Only)"
                                            name="feature_flags.enable_mmid_tooltip"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <LocationProvidersField
                                        description="If enabled, customers using a medical profile will be required to upload an image of their medical recommendation for the selected store providers."
                                        enabled={
                                            flags.is_required_medical_document
                                        }
                                        enabledLabel="Medical Document Required"
                                        enabledName="feature_flags.is_required_medical_document"
                                        error={
                                            requiredMedicalDocumentStoreProvidersError
                                        }
                                        locations={
                                            requiredMedicalDocumentStoreProviderIds
                                        }
                                        locationsDescription="Select the stores where medical document upload will be required. At least one is required."
                                        locationsName="feature_flags.required_medical_document_locations"
                                        locationsTitle="Stores with Medical Document Required enabled"
                                        onEnabledChange={
                                            handleRequiredMedicalDocumentToggle
                                        }
                                        onLocationsChange={
                                            handleRequiredMedicalDocumentStoreProvidersChange
                                        }
                                        options={storeProviderOptions}
                                        title="Medical Document Required"
                                    />
                                    <LocationProvidersField
                                        description="If enabled, medical customers can create medical profiles based on the age requirements set in SellTreez for the selected store providers."
                                        enabled={flags.allow_medical_sign_ups}
                                        enabledLabel="Allow Medical Sign Ups"
                                        enabledName="feature_flags.allow_medical_sign_ups"
                                        error={
                                            medicalSignUpsStoreProvidersError
                                        }
                                        locations={
                                            medicalSignUpsStoreProviderIds
                                        }
                                        locationsDescription="Select the stores where Medical Sign Ups will be available. At least one is required."
                                        locationsName="feature_flags.allowed_medical_sign_up_locations"
                                        locationsTitle="Stores with Medical Sign Ups enabled"
                                        onEnabledChange={
                                            handleMedicalSignUpsToggle
                                        }
                                        onLocationsChange={
                                            handleMedicalSignUpsStoreProvidersChange
                                        }
                                        options={storeProviderOptions}
                                        title="Allow Medical Sign Ups"
                                    />
                                </SectionTable>
                            </Accordion.Content>
                        </Accordion.Item>

                        <Accordion.Item
                            data-section="checkout"
                            value="checkout"
                        >
                            <Accordion.Header>
                                <Accordion.Trigger>
                                    3. Checkout
                                </Accordion.Trigger>
                            </Accordion.Header>
                            <Accordion.Content>
                                <SectionTable>
                                    <SettingRow
                                        description="If on, map will be hidden on Checkout and Thank You page."
                                        title="Hide Map at Checkout"
                                    >
                                        <FlagSwitch
                                            checked={flags.hide_map_at_checkout}
                                            label="Hide Map at Checkout"
                                            name="feature_flags.hide_map_at_checkout"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <LocationProvidersField
                                        description="If enabled, customers will have the option to checkout as a guest for the selected store providers."
                                        enabled={flags.allow_guest_checkout}
                                        enabledLabel="Allow guest checkout"
                                        enabledName="feature_flags.allow_guest_checkout"
                                        error={guestCheckoutStoreProvidersError}
                                        locations={
                                            guestCheckoutStoreProviderIds
                                        }
                                        locationsDescription="Select the stores where Guest Checkout will be available. At least one is required."
                                        locationsName="feature_flags.allowed_guest_checkout_locations"
                                        locationsTitle="Stores with Guest Checkout enabled"
                                        onEnabledChange={
                                            handleGuestCheckoutToggle
                                        }
                                        onLocationsChange={
                                            handleGuestCheckoutStoreProvidersChange
                                        }
                                        options={storeProviderOptions}
                                        title="Allow guest checkout"
                                    />
                                    <LocationProvidersField
                                        autocompleteLabel="Stores with Curbside enabled"
                                        description="If enabled, customers will have the option to select Curbside Ordering at checkout for the selected store providers."
                                        enabled={flags.allow_curbside_pickup}
                                        enabledLabel="Curbside Ordering"
                                        enabledName="feature_flags.allow_curbside_pickup"
                                        error={curbsideStoreProvidersError}
                                        locations={curbsideStoreProviderIds}
                                        locationsDescription="Select the stores where Curbside Ordering will be available at checkout. At least one is required."
                                        locationsName="feature_flags.allowed_curbside_pickup_locations"
                                        locationsTitle="Stores with Curbside enabled"
                                        onEnabledChange={handleCurbsideToggle}
                                        onLocationsChange={
                                            handleCurbsideStoreProvidersChange
                                        }
                                        options={storeProviderOptions}
                                        title="Curbside Ordering"
                                    />
                                    <SettingRow
                                        description="If On, customers can stack coupon based manual discounts and loyalty rewards on the same order."
                                        title="Allow Coupon & Loyalty Stacking"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.allow_discount_loyalty_stacking
                                            }
                                            label="Allow Coupon & Loyalty Stacking"
                                            name="feature_flags.allow_discount_loyalty_stacking"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="The Checkout countdown feature displays a visible timer indicating how much time is left before the checkout resets due to inactivity."
                                        title="Checkout countdown"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Input
                                                error={countdownError}
                                                fullWidth
                                                label="Checkout countdown"
                                                name="feature_flags.checkout_countdown_time"
                                                onChange={inputChange}
                                                placeholder="Value in minutes"
                                                suffix="minutes"
                                                type="number"
                                                value={
                                                    flags.checkout_countdown_time ??
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                    <SettingRow
                                        description={
                                            <>
                                                Placeholder text for the
                                                curbside pickup note field.{" "}
                                                <i>(40 character limit)</i>
                                            </>
                                        }
                                        title="Curbside Pickup Placeholder"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Textarea
                                                error={curbsideError}
                                                fullWidth
                                                label="Curbside Pickup Placeholder"
                                                name="feature_flags.curbside_pickup_placeholder"
                                                onChange={inputChange}
                                                placeholder="Note"
                                                rows={2}
                                                showCount={40}
                                                value={
                                                    flags.curbside_pickup_placeholder ||
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                    <SettingRow
                                        description={
                                            <>
                                                Custom text shown in Payment
                                                Details at checkout when pickup
                                                is selected. Leave empty to use
                                                the default.{" "}
                                                <i>(75 character limit)</i>
                                            </>
                                        }
                                        title="Payment Detail Note Pickup"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Textarea
                                                error={paymentNotePickupError}
                                                fullWidth
                                                label="Payment Detail Note Pickup"
                                                name="feature_flags.payment_detail_note_pickup"
                                                onChange={inputChange}
                                                placeholder="Payment detail note"
                                                rows={2}
                                                showCount={75}
                                                value={
                                                    flags.payment_detail_note_pickup ||
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                    <SettingRow
                                        description={
                                            <>
                                                Custom text shown in Payment
                                                Details at checkout when
                                                delivery is selected. Leave
                                                empty to use the default.{" "}
                                                <i>(75 character limit)</i>
                                            </>
                                        }
                                        title="Payment Detail Note Delivery"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Textarea
                                                error={paymentNoteDeliveryError}
                                                fullWidth
                                                label="Payment Detail Note Delivery"
                                                name="feature_flags.payment_detail_note_delivery"
                                                onChange={inputChange}
                                                placeholder="Payment detail note"
                                                rows={2}
                                                showCount={75}
                                                value={
                                                    flags.payment_detail_note_delivery ||
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                </SectionTable>
                            </Accordion.Content>
                        </Accordion.Item>

                        <Accordion.Item
                            data-section="validation"
                            value="validation"
                        >
                            <Accordion.Header>
                                <Accordion.Trigger>
                                    4. DL Validation
                                </Accordion.Trigger>
                            </Accordion.Header>
                            <Accordion.Content>
                                <SectionTable>
                                    <SettingRow
                                        description={`If on, customers must upload a valid driver's license for verification. Guest checkout bypasses ID verification. Disable Guest Checkout to require ID.`}
                                        title="Verify on Delivery"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.allow_id_verification_for_delivery
                                            }
                                            label="Verify on Delivery"
                                            name="feature_flags.allow_id_verification_for_delivery"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description={`If on, customers must upload a valid driver's license for verification. Guest checkout bypasses ID verification. Disable Guest Checkout to require ID.`}
                                        title="Verify on Pickup"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.allow_id_verification_for_pickup
                                            }
                                            label="Verify on Pickup"
                                            name="feature_flags.allow_id_verification_for_pickup"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description={`The description to be displayed on the driver's license upload screen.`}
                                        title="Title"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Input
                                                fullWidth
                                                label="Title"
                                                name="feature_flags.id_verification_title"
                                                onChange={inputChange}
                                                placeholder="Title"
                                                value={
                                                    flags.id_verification_title ??
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                    <SettingRow
                                        description={`The title to be displayed on the driver's license upload screen.`}
                                        title="Description"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Textarea
                                                fullWidth
                                                label="Description"
                                                name="feature_flags.id_verification_description"
                                                onChange={inputChange}
                                                placeholder="Description"
                                                rows={4}
                                                value={
                                                    flags.id_verification_description ??
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                </SectionTable>
                            </Accordion.Content>
                        </Accordion.Item>

                        <Accordion.Item
                            data-section="product_detail"
                            value="product_detail"
                        >
                            <Accordion.Header>
                                <Accordion.Trigger>
                                    5. Product Detail
                                </Accordion.Trigger>
                            </Accordion.Header>
                            <Accordion.Content>
                                <SectionTable>
                                    <SettingRow
                                        description='If on, "Continue Shopping" shows on product detail.'
                                        title="Continue shopping"
                                    >
                                        <FlagSwitch
                                            checked={flags.keep_shopping_active}
                                            label="Continue shopping"
                                            name="feature_flags.keep_shopping_active"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    {/* <SettingRow
                                    description="If on, product reviews do NOT show."
                                    title="Products review"
                                >
                                    <FlagSwitch
                                        checked={
                                            flags.disable_product_reviews
                                        }
                                        label="Products review"
                                        name="feature_flags.disable_product_reviews"
                                        onChange={inputChange}
                                    />
                                </SettingRow> */}
                                </SectionTable>
                            </Accordion.Content>
                        </Accordion.Item>

                        <Accordion.Item data-section="kios" value="kios">
                            <Accordion.Header>
                                <Accordion.Trigger>6. Kiosk</Accordion.Trigger>
                            </Accordion.Header>
                            <Accordion.Content>
                                <SectionTable>
                                    <SettingRow
                                        description="If on, ordering is not allowed in Kiosk mode."
                                        title="Disable Add to Cart in Kiosk"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.is_add_to_cart_in_kiosk_disabled
                                            }
                                            label="Disable Add to Cart in Kiosk"
                                            name="feature_flags.is_add_to_cart_in_kiosk_disabled"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, the customer's first name will be required at checkout."
                                        title="Checkout - First Name required"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.kiosk_first_name_required
                                            }
                                            label="Checkout - First Name required"
                                            name="feature_flags.kiosk_first_name_required"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, the customer's last name will be required at checkout."
                                        title="Checkout - Last Name required"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.kiosk_last_name_required
                                            }
                                            label="Checkout - Last Name required"
                                            name="feature_flags.kiosk_last_name_required"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, the customer's email will be required at checkout."
                                        title="Checkout - Email required"
                                    >
                                        <FlagSwitch
                                            checked={flags.kiosk_email_required}
                                            label="Checkout - Email required"
                                            name="feature_flags.kiosk_email_required"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, the customer's phone will be required at checkout."
                                        title="Checkout - Phone required"
                                    >
                                        <FlagSwitch
                                            checked={flags.kiosk_phone_required}
                                            label="Checkout - Phone required"
                                            name="feature_flags.kiosk_phone_required"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, the customer's date of birth will be required at checkout."
                                        title="Checkout - Date of birth required"
                                    >
                                        <FlagSwitch
                                            checked={flags.kiosk_dob_required}
                                            label="Checkout - Date of birth required"
                                            name="feature_flags.kiosk_dob_required"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="If on, form fields that are not required will be hidden from the Kiosk checkout."
                                        title="Hide Optional Fields"
                                    >
                                        <FlagSwitch
                                            checked={
                                                flags.kiosk_hide_optional_contact_fields
                                            }
                                            label="Hide Optional Fields"
                                            name="feature_flags.kiosk_hide_optional_contact_fields"
                                            onChange={inputChange}
                                        />
                                    </SettingRow>
                                    <SettingRow
                                        description="The kiosk idle timeout setting determines how long the system will remain inactive before automatically resetting or returning to the home screen."
                                        title="Kiosk idle timeout"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Input
                                                fullWidth
                                                label="Kiosk idle timeout"
                                                name="feature_flags.kiosk_timeout_seconds"
                                                onChange={inputChange}
                                                placeholder="Value in seconds"
                                                suffix="sec"
                                                type="number"
                                                value={
                                                    flags.kiosk_timeout_seconds ??
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                    <SettingRow
                                        description="The kiosk countdown feature displays a visible timer indicating how much time is left before the kiosk resets due to inactivity."
                                        title="Kiosk countdown"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Input
                                                fullWidth
                                                label="Kiosk countdown"
                                                name="feature_flags.kiosk_countdown_time"
                                                onChange={inputChange}
                                                placeholder="Value in seconds"
                                                suffix="sec"
                                                type="number"
                                                value={
                                                    flags.kiosk_countdown_time ??
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                    <SettingRow
                                        description="This setting defines how long the Thank You Page or Confirmation Page is displayed after a user completes a transaction or interaction at the kiosk."
                                        title="Kiosk Thank You Page timeout seconds"
                                    >
                                        <div className={styles.rowControlField}>
                                            <Input
                                                fullWidth
                                                label="Kiosk Thank You Page timeout seconds"
                                                name="feature_flags.kiosk_thank_you_timeout_seconds"
                                                onChange={inputChange}
                                                placeholder="Value in seconds"
                                                suffix="sec"
                                                type="number"
                                                value={
                                                    flags.kiosk_thank_you_timeout_seconds ??
                                                    ""
                                                }
                                            />
                                        </div>
                                    </SettingRow>
                                </SectionTable>
                            </Accordion.Content>
                        </Accordion.Item>
                    </Accordion>
                )}
            </div>
        </Panel>
    );
};

export default CustomizationsForm;
